import { useState } from 'react';
import { Button, Checkbox, Modal } from '@shared/components';
import { useUpdateUserPermissionsMutation } from '../hooks/useUpdateUserPermissionsMutation';
import type {
  UserPermissionEntry,
  UserDealerPermissionEntry,
  DealerRef,
  UserPermissionsMapEntry,
} from '../types/security-admin.types';

interface PermissionSet {
  can_quote: boolean;
  can_invoice: boolean;
  can_sell: boolean;
}

interface Props {
  groupId: string;
  userId: string;
  existingPermissions: UserPermissionEntry | undefined;
  existingDealerPermissions: UserDealerPermissionEntry[];
  groupDealers: DealerRef[];
  onClose: () => void;
  /** When provided, the user-side query cache is invalidated after a successful save. */
  invalidateUserId?: string;
}

export function UserPermissionsEditor({
  groupId,
  userId,
  existingPermissions,
  existingDealerPermissions,
  groupDealers,
  onClose,
  invalidateUserId,
}: Props) {
  const initialGroupPerms: PermissionSet = existingPermissions?.permissions ?? {
    can_quote: false,
    can_invoice: false,
    can_sell: false,
  };

  const [groupPerms, setGroupPerms] = useState<PermissionSet>(initialGroupPerms);
  const [dealerOverrides, setDealerOverrides] = useState<Record<string, PermissionSet>>(() =>
    Object.fromEntries(existingDealerPermissions.map((udp) => [udp.dealer_id, { ...udp.permissions }])),
  );
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateUserPermissionsMutation(groupId, invalidateUserId);

  const updateGroupPerm = (key: keyof PermissionSet, value: boolean) => {
    setGroupPerms((prev) => ({ ...prev, [key]: value }));
  };

  // Non-overridden dealer rows read the live group-level values, not a snapshot.
  const getDealerPerms = (dealerId: string): PermissionSet => dealerOverrides[dealerId] ?? groupPerms;

  const updateDealerPerm = (dealerId: string, key: keyof PermissionSet, value: boolean) => {
    setDealerOverrides((prev) => ({
      ...prev,
      [dealerId]: { ...(prev[dealerId] ?? groupPerms), [key]: value },
    }));
  };

  const handleSave = () => {
    setError(null);
    // The backend deletes every dealer override before recreating from the
    // payload, so the complete override map must be sent to keep untouched rows.
    const userPermissions: Record<string, UserPermissionsMapEntry> = {
      group: groupPerms,
    };
    for (const [dealerId, permissions] of Object.entries(dealerOverrides)) {
      userPermissions[dealerId] = { permissions };
    }

    updateMutation.mutate(
      {
        userId,
        // The user comes from the route param; the payload carries only `user_permissions`.
        payload: {
          user_permissions: userPermissions,
        },
      },
      {
        onSuccess: () => onClose(),
        onError: () => setError('Failed to save permissions. Please try again.'),
      },
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      className="max-w-lg mx-4 max-h-screen overflow-y-auto"
      ariaLabel="Edit User Permissions"
    >
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Edit User Permissions</h2>

        <p className="text-sm text-mp-muted font-mono">{userId}</p>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Group-level Permissions</h3>
          <p className="text-xs text-mp-muted">
            Any of these lets the member see the agreements, quotes and customers inside this group's reach. Can quote saves quotes, Can sell completes them, and Can invoice raises invoices against agreements in the reach.
          </p>
          <div className="flex gap-4">
            <Checkbox
              id="group-can-quote"
              label="Can Quote"
              checked={groupPerms.can_quote}
              onChange={(v) => updateGroupPerm('can_quote', v)}
              disabled={updateMutation.isPending}
            />
            <Checkbox
              id="group-can-invoice"
              label="Can Invoice"
              checked={groupPerms.can_invoice}
              onChange={(v) => updateGroupPerm('can_invoice', v)}
              disabled={updateMutation.isPending}
            />
            <Checkbox
              id="group-can-sell"
              label="Can Sell"
              checked={groupPerms.can_sell}
              onChange={(v) => updateGroupPerm('can_sell', v)}
              disabled={updateMutation.isPending}
            />
          </div>
        </div>

        {groupDealers.length > 0 && (
          <div className="space-y-3 border-t border-mp-border pt-4">
            <h3 className="text-sm font-semibold">Per-Dealer Overrides <span className="text-mp-muted font-normal">(optional)</span></h3>
            {groupDealers.map((dealer) => {
              const perms = getDealerPerms(dealer.id);
              return (
                <div key={dealer.id} className="space-y-1">
                  <p className="text-sm font-medium">{dealer.name}</p>
                  <div className="flex gap-4 pl-2 flex-wrap">
                    <Checkbox
                      id={`dealer-${dealer.id}-can-quote`}
                      label="Can Quote"
                      checked={perms.can_quote}
                      onChange={(v) => updateDealerPerm(dealer.id, 'can_quote', v)}
                      disabled={updateMutation.isPending}
                    />
                    <Checkbox
                      id={`dealer-${dealer.id}-can-invoice`}
                      label="Can Invoice"
                      checked={perms.can_invoice}
                      onChange={(v) => updateDealerPerm(dealer.id, 'can_invoice', v)}
                      disabled={updateMutation.isPending}
                    />
                    <Checkbox
                      id={`dealer-${dealer.id}-can-sell`}
                      label="Can Sell"
                      checked={perms.can_sell}
                      onChange={(v) => updateDealerPerm(dealer.id, 'can_sell', v)}
                      disabled={updateMutation.isPending}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {error && <p className="text-mp-error text-sm">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Permissions'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
